import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Plus } from "lucide-react";
import { preventivoApi, type Ultimo } from "@/api/preventivo";
import { leggiCondivisi, scartaCondivisi } from "@/pwa/condivisi";
import { AvvisoErrore } from "@/components/preventivo/BolletteFuori";
import { CampoCerca, ListaPreventivi } from "@/components/preventivo/ListaPreventivi";
import { MASSIMO_BOLLETTE, SceltaPreventivo } from "@/components/preventivo/SceltaPreventivo";
import { SceltaCondivisi } from "@/components/preventivo/SceltaCondivisi";
import { filtraPerAmministratore, useElenco } from "@/components/preventivo/useElenco";
import { ALLEGATI_NON_RICEVUTI, COLLEGAMENTO_NON_RIUSCITO, NUOVO_PREVENTIVO, raggiuntoMassimo } from "@/components/preventivo/scritte";
import { TITOLO_LISTA } from "@/components/preventivo/scritteLista";
import { FOCUS } from "@/components/preventivo/parti";

/** I file di una condivisione di Android, con il suo identificativo (serve a scartarla senza toccarne un'altra). */
type Condivisione = { id: string; files: File[] };

const ID_PREVENTIVO = (id: string) => `/preventivo-condomini/${id}`;
/** Dove portano le scelte della condivisione `c`: la pagina del preventivo legge i file e li aggiunge, senza chiedere, solo
 *  se in attesa c'è proprio quella (una voce sola in IndexedDB: una condivisione più nuova la sovrascrive). */
const CON_FILE = (id: string, c: string) => `${ID_PREVENTIVO(id)}?condivisi=${encodeURIComponent(c)}`;

const PRIMARIO =
  `inline-flex h-12 w-full items-center justify-center gap-2 rounded-lg bg-brand-dark px-5 text-base font-semibold text-white cursor-pointer touch-manipulation transition-colors enabled:hover:bg-dark disabled:opacity-60 disabled:cursor-not-allowed md:h-11 md:w-auto ${FOCUS} focus-visible:ring-offset-2`;

/** La pagina d'ingresso dello strumento (spec 2026-10-28 §§ 5 e 8): la lista dei preventivi salvati, con «Nuovo preventivo» e
 *  la ricerca per amministratore. Con `?condivisi=1` (bollette inoltrate da WhatsApp) prima chiede in quale preventivo
 *  metterle. La ricerca si fa nel browser (il nome non deve stare negli indirizzi che finiscono nei log, spec § 10) e il
 *  testo cercato sta nello stato della cronologia, non nell'indirizzo: tornando indietro dal preventivo la lista è com'era. */
export function PreventiviCondomini() {
  const navigate = useNavigate();
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const [testo, setTesto] = useState(() => (location.state as { q?: string } | null)?.q ?? "");
  const q = testo.trim();
  // la ricerca è locale e immediata: niente attesa. Lo stato della voce di cronologia si aggiorna sostituendola (ogni
  // lettera non è un passo della cronologia) e si tengono indirizzo e parametri com'erano
  const cerca = (nuovo: string) => {
    setTesto(nuovo);
    navigate(`${location.pathname}${location.search}`, { replace: true, state: { ...(location.state as object | null), q: nuovo } });
  };
  const { voci: tutte, errore, riprova } = useElenco();
  const voci = useMemo(() => (tutte === null ? null : filtraPerAmministratore(tutte, q)), [tutte, q]);

  // «Nuovo preventivo»: il server riusa quello vuoto, se c'è; un tocco solo (ref) e il tasto resta occupato
  const [occupato, setOccupato] = useState(false);
  const [nuovoFallito, setNuovoFallito] = useState(false);
  const occupatoRef = useRef(false);
  const apriNuovo = useCallback(async (verso: (id: string) => void): Promise<boolean> => {
    if (occupatoRef.current) return true;
    occupatoRef.current = true;
    setOccupato(true);
    setNuovoFallito(false);
    try {
      const { id } = await preventivoApi.nuovo();
      verso(id);
      return true;
    } catch {
      setNuovoFallito(true);
      occupatoRef.current = false;
      setOccupato(false);
      return false;
    }
  }, []);

  // ——— bollette inoltrate da WhatsApp ———
  const condivisiRef = useRef(params.get("condivisi"));
  const [letti, setLetti] = useState<Condivisione | null | undefined>(undefined);   // undefined = non ancora letti
  const [ultimo, setUltimo] = useState<Ultimo | null | undefined>(undefined);
  const [scelta, setScelta] = useState<Condivisione | null>(null);
  // «In quale preventivo?» è un passo della cronologia: il «indietro» del telefono torna alle tre scelte
  const vistaAltro = params.get("scegli") === "1";
  const [decisa, setDecisa] = useState(condivisiRef.current !== "1");
  const [allegatiNonRicevuti, setAllegatiNonRicevuti] = useState(false);
  const togliCondivisi = useCallback(() => {
    setParams((p) => { const n = new URLSearchParams(p); n.delete("condivisi"); n.delete("scegli"); return n; }, { replace: true });
  }, [setParams]);
  // Si lascia la lista per un preventivo: prima la voce della lista perde `condivisi` (tornando indietro non si riofferiscono
  // file già entrati); una volta sola, anche con un secondo tocco.
  const navigatoRef = useRef(false);
  const vai = (percorso: string) => {
    if (navigatoRef.current) return;
    navigatoRef.current = true;
    togliCondivisi();
    navigate(percorso);
  };

  const avviatoRef = useRef(false);
  useEffect(() => {
    if (condivisiRef.current === "errore") { condivisiRef.current = null; setAllegatiNonRicevuti(true); togliCondivisi(); return; }
    if (condivisiRef.current !== "1" || avviatoRef.current) return;
    avviatoRef.current = true;
    leggiCondivisi().then(setLetti, () => { setLetti(null); setDecisa(true); setAllegatiNonRicevuti(true); togliCondivisi(); });
    Promise.resolve().then(() => preventivoApi.ultimo()).then(setUltimo, () => setUltimo(null));
  }, [togliCondivisi]);

  // si decide quando ci sono i file, l'ultimo preventivo e l'elenco (anche se non è arrivato: la scelta resta possibile)
  const decisoRef = useRef(false);
  const pronto = letti !== undefined && ultimo !== undefined && (tutte !== null || errore);
  useEffect(() => {
    if (!pronto || decisoRef.current || !letti) return;
    decisoRef.current = true;
    const haUltimo = ultimo !== null && ultimo !== undefined && ultimo.totale > 0;
    const altri = (tutte ?? []).some((v) => v.id !== ultimo?.id);
    if (haUltimo || altri || errore) { setScelta(letti); setDecisa(true); return; }
    // nessun preventivo dove metterle: ne nasce uno, e indietro non deve tornare a questo passaggio
    void apriNuovo((id) => navigate(CON_FILE(id, letti.id), { replace: true })).then((ok) => { if (!ok) setScelta(letti); setDecisa(true); });
  }, [pronto, letti, ultimo, tutte, errore, apriNuovo, navigate]);
  useEffect(() => {
    if (letti === null) { decisoRef.current = true; setDecisa(true); togliCondivisi(); }
  }, [letti, togliCondivisi]);

  const annulla = () => {
    if (scelta) scartaCondivisi(scelta.id).catch(() => { /* resta in attesa: la prossima apertura la ritrova */ });
    setScelta(null);
    togliCondivisi();
  };

  if (scelta !== null) {
    if (vistaAltro) {
      return (
        <SceltaPreventivo
          n={scelta.files.length}
          escludi={ultimo?.id}
          href={(id) => CON_FILE(id, scelta.id)}
          onIndietro={() => navigate(-1)}
          onApri={(e) => {
            if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;     // una nuova scheda non lascia la lista
            if (navigatoRef.current) { e.preventDefault(); return; }
            navigatoRef.current = true;
            togliCondivisi();
          }}
        />
      );
    }
    const usabile = ultimo && ultimo.totale > 0 ? ultimo : null;
    return (
      <SceltaCondivisi
        file={scelta.files.map((f) => ({ nome: f.name, tipo: f.type }))}
        ultimo={usabile}
        altri={(tutte ?? []).some((v) => v.id !== usabile?.id)}
        onUltimo={() => { if (usabile) vai(CON_FILE(usabile.id, scelta.id)); }}
        onAltro={() => setParams((p) => { const n = new URLSearchParams(p); n.set("scegli", "1"); return n; })}
        onNuovo={() => { void apriNuovo((id) => vai(CON_FILE(id, scelta.id))); }}
        onAnnulla={annulla}
        occupato={occupato}
        tetto={usabile && usabile.totale >= MASSIMO_BOLLETTE ? raggiuntoMassimo(MASSIMO_BOLLETTE) : undefined}
        errore={nuovoFallito}
      />
    );
  }
  if (!decisa) return <div aria-busy="true" className="h-24 rounded-lg border border-border bg-card animate-pulse motion-reduce:animate-none" />;

  const nuovo = (
    <button type="button" onClick={() => { void apriNuovo((id) => navigate(ID_PREVENTIVO(id))); }}
      disabled={occupato} aria-busy={occupato || undefined} className={PRIMARIO}>
      <Plus size={18} aria-hidden="true" />
      {NUOVO_PREVENTIVO}
    </button>
  );
  const vuota = tutte !== null && tutte.length === 0;
  return (
    <div className="space-y-4">
      {allegatiNonRicevuti && (
        <div className="rounded-lg border border-border bg-card p-3">
          <AvvisoErrore testo={ALLEGATI_NON_RICEVUTI} />
        </div>
      )}
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <h1 className="font-heading text-2xl font-semibold text-text">{TITOLO_LISTA}</h1>
        {!vuota && nuovo}
      </div>
      {nuovoFallito && <AvvisoErrore testo={COLLEGAMENTO_NON_RIUSCITO} />}
      {!vuota && <CampoCerca valore={testo} onCambia={cerca} />}
      <ListaPreventivi
        voci={voci}
        q={q}
        errore={errore}
        onRiprova={riprova}
        href={(v) => ID_PREVENTIVO(v.id)}
        azioneVuota={nuovo}
      />
    </div>
  );
}
